import React, { useContext } from 'react';
import styled from '@emotion/styled';
import { PriceContext } from './priceProvider';
import { useTranslation } from 'react-i18next';

type Props = {
  children: number,
  showCurrencySign?: boolean,
  dataTest?: string,
  isNegative?: boolean,
  isPositive?: boolean,
  id?: String,
  ariaHidden?: boolean,
};

const getAriaLabel = (value, priceContext, t, isNegative, isPositive) => {
  let ariaLabel = '';

  if (isNegative) {
    ariaLabel += `${t('minus').toLowerCase()} `;
  }

  if (isPositive) {
    ariaLabel += `${t('plus').toLowerCase()} `;
  }

  const formattedValue = new Intl.NumberFormat('en-GB', {
    currency: priceContext.currency,
    minimumFractionDigits: 2,
    useGrouping: false,
  }).format(value);

  ariaLabel += `${priceContext.currency.toUpperCase()} `;
  ariaLabel += formattedValue;

  return ariaLabel;
};

export const Price = ({ children, showCurrencySign, dataTest, isNegative, isPositive, id, ariaHidden }: Props) => {
  const value = useContext(PriceContext);
  const { t } = useTranslation('common');
  const format =
    value.showCurrencySign || showCurrencySign
      ? { style: 'currency', currency: value.currency }
      : { minimumFractionDigits: 2 };

  const ariaLabel = getAriaLabel(children, value, t, isNegative, isPositive);

  return (
    <Wrapper data-test={dataTest || 'price'} aria-label={ariaLabel} id={id} aria-hidden={ariaHidden}>
      {isNegative && '-'}
      {isPositive && '+'}
      {new Intl.NumberFormat(value.locale, format).format(children)}
    </Wrapper>
  );
};

const Wrapper = styled('span')`
  white-space: nowrap;
`;
